/**
 * The head of a table card: whose table it is, how many dishes are open, the
 * served-items toggle, and the button that opens this table's comanda across
 * the whole display.
 *
 * A table has one comanda. The button opens it — it never asks the cook which.
 */
import { Eye, EyeOff, Maximize2 } from "lucide-react";
import { useTranslation } from "../i18n";

interface TableCardHeaderProps {
  tableName: string;
  isTakeaway: boolean;
  activeCount: number;
  servedCount: number;
  showServed: boolean;
  onToggleServed: () => void;
  /** Absent inside the full-screen view, which already is the full screen. */
  onOpenFull?: () => void;
  waveChange?: { targetWave: number } | null;
}

export default function TableCardHeader({
  tableName,
  isTakeaway,
  activeCount,
  servedCount,
  showServed,
  onToggleServed,
  onOpenFull,
  waveChange,
}: TableCardHeaderProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-kds-muted/20 flex-shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        {isTakeaway && (
          <span className="text-kds-muted text-kds-dish" title={t("kds.table.takeawayCounter", "Asporto / Banco")}>
            🛍
          </span>
        )}
        <h2 className="text-kds-title text-kds-text leading-tight truncate">{tableName}</h2>
        {waveChange && (
          <span
            className="text-kds-badge uppercase px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/40 animate-pulse shrink-0"
            title={t("kds.table.waveMoved", "Il cameriere ha spostato piatti su un'altra ondata")}
          >
            {t("kds.table.waveMovedBadge", "↔ {{wave}}ª ondata", { wave: waveChange.targetWave })}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <span className="text-kds-meta text-kds-muted bg-kds-card px-2.5 py-0.5 rounded-full">
          {activeCount}
        </span>

        {servedCount > 0 && (
          <button
            type="button"
            onClick={onToggleServed}
            className={[
              "flex items-center gap-2 text-kds-badge px-2 py-1 rounded border transition-colors min-h-touch",
              showServed
                ? "border-kds-muted/60 bg-kds-card text-kds-text"
                : "border-kds-muted/30 text-kds-muted hover:text-kds-text hover:border-kds-muted/50",
            ].join(" ")}
            title={
              showServed
                ? t("kds.table.hideServed", "Nascondi serviti")
                : t("kds.table.showServed", "Mostra {{count}} serviti", { count: servedCount })
            }
          >
            {showServed ? (
              <>
                <EyeOff size={20} />
                {t("kds.table.hide", "Nascondi")}
              </>
            ) : (
              <>
                <Eye size={20} />+{servedCount}
              </>
            )}
          </button>
        )}

        {onOpenFull && (
          <button
            type="button"
            onClick={onOpenFull}
            className="inline-flex items-center justify-center gap-1.5 px-3 rounded-lg bg-kds-card text-kds-text text-kds-action min-h-touch min-w-touch hover:bg-kds-card/70 transition-colors"
            title={t("kds.order.openFull", "Apri la comanda a tutto schermo")}
          >
            <Maximize2 size={22} strokeWidth={3} />
            {t("kds.order.open", "Apri")}
          </button>
        )}
      </div>
    </div>
  );
}
